import { Action, Step, getOverdueInvoices, maskAccountNumber, sumAmounts } from '../core/utils'
import { selectWarehouse } from 'containers/Warehouses/warehousesSlice'
import DeliveryPopup from 'components/popups/DeliveryPopup'
import { useHistory, useParams } from 'react-router-dom'
import { SECTION_TYPES } from 'containers/Warehouse'
import ArrowBtn from 'components/buttons/ArrowBtn'
import { LOCATION_STATES } from 'utils/constants'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import usePopupState from 'hooks/usePopupState'
import { displayPrice } from 'utils/string'
import KeyReturn from '../core/KeyReturn'
import { useReduxSelector } from 'store'
import Deposits from '../core/Deposits'
import { toLongDate } from 'utils/date'
import Option from 'components/Option'
import React from 'react'

interface DefaultProps {
  isSending: boolean
  response?: payments.Quote
  selectedData: payments.SelectedData
  deliveries: payments.Delivery[]
  sendResignation: basic.BasicFunc
  selectData: React.Dispatch<React.SetStateAction<payments.SelectedData>>
  setDeliveries: React.Dispatch<React.SetStateAction<payments.Delivery[]>>
  setStep: React.Dispatch<React.SetStateAction<Step>>
  showCameraWarning: basic.BasicFunc
}

const type = process.env.REACT_APP_TYPE

const Default: React.FC<DefaultProps> = ({ isSending, response, selectedData, deliveries, selectData, setDeliveries, sendResignation, showCameraWarning, setStep }) => {
  const { id } = useParams<warehouse.PageParams>()
  const warehouse = useReduxSelector(state => selectWarehouse(state, id))
  const { popup } = usePopupState()

  const { push, goBack } = useHistory()
  const { t, lang } = useTranslation('warehouse')
  const openAction = (action: Action) => {
    push(`/warehouses/${id}/${SECTION_TYPES.termination}/${action}`)
  }

  const overdueInvoices = getOverdueInvoices(response)
  const unpaidInvoicesAmount = sumAmounts(overdueInvoices.map(invoice => invoice.remainingAmount))
  // const deposit = getTotalDeposits(selectedData, response?.warehouses?.[0])
  // const refundGrossPrice = Number(response?.warehouses?.[0]?.refundGrossAmount || 0)
  const photosLength = selectedData.photos?.length || selectedData.documents?.length || 0
  const photosAdded = (selectedData.photos && selectedData.photos.length > 0) || (selectedData.documents && selectedData.documents.length > 0)
  // realny zwrot pieniedzy (refund.paidGross); correctionGross to kwota korekty, ktora moze tylko pomniejszac dlug
  const totalToBeRefunded = response?.flags?.hasRefund
    ? Number(response?.refund?.paidGross || response?.amounts?.refundPaidGross || 0)
    : 0
  const hasActiveDeposits = !!response?.deposits?.some(({ isRefunded }) => !isRefunded)

  const itemsToReturn = response?.flags?.itemsToReturn && response?.flags?.itemsToReturn.length > 0
    ? response?.flags?.itemsToReturn?.map((item) => {
      const { id, name, deposit } = item
      const deliveryId = selectedData.itemsToReturn?.[id]
      return <Option
        key={id}
        dashedBorder
        secondaryTheme
        selected={!deliveryId}
        title={`${t('return')} ${name}`}
        buttonText={t(deliveryId ? 'change' : 'return')}
        desc={`${t('depositCollected')}: ${displayPrice(deposit)}`}
        action={() => {
          push(window.location.pathname, LOCATION_STATES.popup({ type: 'returns', data: item } as unknown as string))
        }}
      />
    })
    : undefined

  return <>
    {(popup as any)?.type === 'returns' && <div>
      {((data: payments.CancelRentalResponseWarehouseReturn) => {
        return <DeliveryPopup
          popupName={t('returns')}
          price={data.deposit}
          cancelAction={goBack}
          productName={data.name}
          deliveryMethods={data.returnMethods}
          recentlySelectedDeliveries={deliveries}
          setRecentlySelectedDeliveries={setDeliveries}
          callback={({ deliveryId }) => {
            if (deliveryId) {
              selectData(prev => ({ ...prev, itemsToReturn: { ...(prev.itemsToReturn || {}), [data.id]: deliveryId } }))
            }
            goBack()
          }}
        />
      })((popup as any).data)}
    </div>}

    <div className='title'>
      <div>{t('resignationType.termination')}</div>
      <div>{t('completeTheProcess')}</div>
    </div>

    <div className='resignation__main'>
      <div className='resignation__steps'>
        <Option
          dashedBorder
          secondaryTheme
          selected={!selectedData.date}
          title={t('resignationType.termination')}
          desc={selectedData.date ? toLongDate(selectedData.date, lang, true) : t('notSelected')}
          buttonText={t('change')}
          icon='calendar'
          action={(warehouse.resignation?.dateAccepted || warehouse.resignation?.isCompleted)
            ? undefined
            : () => {
              openAction('calendar')
            }}
          additionalContent={warehouse.resignation?.dateAccepted
            ? <div className='additionalContent'>
              <div className='additionalContent__item'>
                <i className='icon icon--lock s16' />
                <span>{t('terminationCalendar.dateAccepted')}</span>
              </div>
            </div>
            : undefined}
        />

        <div className='title'>
          <div>{t('requiredActions')}</div>
          <div>{t('youHaveToDoItBy')} {toLongDate(selectedData.date || Date.now(), lang)}</div>
        </div>

        {(totalToBeRefunded > 0 || hasActiveDeposits) && <Option
          dashedBorder
          secondaryTheme
          title={t('returnDetails')}
          selected={!selectedData.refundAccountNumber}
          buttonText={t(selectedData.refundAccountNumber ? 'edit' : 'add')}
          desc={selectedData.refundAccountNumber
            ? maskAccountNumber(selectedData.refundAccountNumber)
            : t('fillInTheMissingDetailsForTheRefund')}
          icon='payments'
          action={() => {
            openAction('refund')
          }}
          additionalContent={<div className='additionalContent'>
            {totalToBeRefunded > 0 && <div className='additionalContent__item'>
              <i className='icon icon--payment-success s16 cGreen' />
              <span>{t('youWillReceiveRefund').replace('{amount}', displayPrice(totalToBeRefunded))}</span>
            </div>}
            {hasActiveDeposits && <div className='additionalContent__item'>
              <i className='icon icon--return s16 cGreen' />
              <span>{t('depositWillBeReturnedAfterContractEnds')}</span>
            </div>}
          </div>}
        />}

        <Option
          dashedBorder
          secondaryTheme
          title={t(unpaidInvoicesAmount ? 'unpaidInvoices' : 'payments')}
          selected={!!unpaidInvoicesAmount}
          buttonText={t(unpaidInvoicesAmount ? 'pay' : 'goTo')}
          desc={
            unpaidInvoicesAmount > 0
              ? `${overdueInvoices.length} ${t('invoicesForTheAmount')}: ${displayPrice(unpaidInvoicesAmount)}`
              : t('everythingsAllRight')
          }
          icon='payments'
          action={(unpaidInvoicesAmount /* || tiles */)
            ? () => {
              setStep('payment')
            } : undefined}
        />

        {response?.flags?.needPhoto && <Option
          dashedBorder
          secondaryTheme
          selected={!photosAdded}
          title={t(`takeAPictureOfTheMag.${type}`)}
          desc={photosAdded
            ? photosLength === 1
              ? `${t('added')} ${t('onePhoto')}`
              : `${t('added')} ${photosLength} ${t('photos')}`
            : t(`showTheStatusOfYourMag.${type}`)}
          buttonText={t(photosAdded ? 'change' : 'add')}
          icon='add-photo'
          action={() => {
            if (photosAdded) openAction('camera')
            else showCameraWarning()
          }}
          additionalContent={<div className='additionalContent'>
            <div className='additionalContent__item'>
              <i className='icon icon--warning2 s16 cPrimary' />
              <span>{t('storageShouldBeEmptyAndClean')}</span>
            </div>
            <div className='additionalContent__item'>
              <i className='icon icon--warning2 s16 cPrimary' />
              <span>{t('accessWillBeBlockedAfterAddingPhoto')}</span>
            </div>
            <div className='additionalContent__item'>
              <i className='icon icon--warning2 s16 cPrimary' />
              <span>{t('pleaseTakeAPhotoOfTheObjectBy')} {toLongDate(response.endDate, lang)}{response?.flags?.checkoutTime ? ` ${t('at')} ${response?.flags?.checkoutTime}` : ''}.{response?.flags?.withoutPhotoCost ? ` ${t('aPenaltyOf')}${lang === 'en' ? ` ${displayPrice(response?.flags?.withoutPhotoCost)}` : ''} ${t('willBeChargedAfterThisDeadline')}${lang === 'pl' ? ` ${displayPrice(response?.flags?.withoutPhotoCost)}.` : '.'}` : ''}</span>
            </div>
          </div>}
        />}

        {itemsToReturn
          ? <>
            <p className='--center'>{t('chooseHowToReturnYourItems')}</p>
            {itemsToReturn}
          </>
          : null}

        <KeyReturn keyReturn={response?.keyReturn ?? warehouse.keyReturn} />

        <Deposits
          deposits={response?.deposits}
          depositsSummary={response?.depositsSummary}
        />
      </div>

      <div className='resignation__buttons'>
        <ArrowBtn
          enlarged
          text={t('confirm')}
          isLoading={isSending}
          action={sendResignation}
        />
        {(!warehouse.resignation || warehouse.resignation.canStop) && <BackBtn />}
      </div>
    </div>
  </>
}

export default Default